// =============================================================================
// FRONTEND API CLIENT CORE - LIGHTWEIGHT
// frontend/src/lib/apiClient.ts
// =============================================================================
import axios, { AxiosResponse, AxiosError, type AxiosInstance } from 'axios';
import { errorStatusForLog } from './apiError';
import { isAllowedProdApiUrl } from './apiOrigin';
import { getErrorCode as mfaErrorCode } from './apiError';
import { clearPlatformMfaToken, getMfaGeneration, getPlatformMfaToken, needsMfaHeader, hasTicketParam, MFA_HEADER, MFA_FORBIDDEN_EVENT, MFA_REQUIRED_EVENT } from './platformMfaToken';
import { debugLog, debugError, DEBUG_CONFIG } from '../config/debug';
import type { User, LoginRequest, LoginResponse } from '../types';
import { applyEmployeeTaskPayloadRule } from './taskPayload';
import i18n from 'i18next';
import { installErrorDetailNormalizer, configureErrorLocale, type TLike } from './apiError';

configureErrorLocale(() => i18n.language);

export {
  getErrorMessage, getErrorInfo, getFieldErrors, getRequestId, getErrorCode, getErrorParams, getErrorDetail,
  getErrorRawMessage, getBodyErrorMessage, getBodyErrorInfo, errorStatusForLog,
} from './apiError';

/** t del cat?logo para el normalizador (si i18n a?n no carga, cae al message del backend). */
export const errorT = (): TLike | undefined => (i18n.isInitialized ? (i18n.t.bind(i18n) as unknown as TLike) : undefined);
/** Fallback i18n especifico de pantalla para sitios sin hook (namespace por defecto). */
export const screenFallback = (key: string, def: string): string => {
  try {
    const s = i18n.isInitialized ? i18n.t(key, { defaultValue: def }) : def;
    return typeof s === 'string' && s && s !== key ? s : def;
  } catch {
    return def;
  }
};
/** Instala el normalizador de detail (cat?logo v7.0.5) en otra instancia axios. Debe ser su PRIMER interceptor de respuesta. */
export const installApiErrorNormalizer = (instance: AxiosInstance) =>
  installErrorDetailNormalizer(instance as unknown as Parameters<typeof installErrorDetailNormalizer>[0], errorT);

// ===== API CONFIGURATION =====
const normalizeApiUrl = (url: string) => {
  let normalizedUrl = url.replace(/\/$/, '');

  if (normalizedUrl.endsWith('/api')) {
    normalizedUrl = normalizedUrl.slice(0, -4);
  }

  // Prevent mixed-content failures if an old build/env still points at http.
  if (
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:' &&
    normalizedUrl.startsWith('http://')
  ) {
    try {
      const parsedUrl = new URL(normalizedUrl);

      if (
        parsedUrl.hostname === window.location.hostname ||
        parsedUrl.hostname === 'synclyst.com'
      ) {
        parsedUrl.protocol = 'https:';
        normalizedUrl = parsedUrl.toString().replace(/\/$/, '');
      }
    } catch {
      normalizedUrl = normalizedUrl.replace(/^http:\/\//, 'https://');
    }
  }

  return normalizedUrl;
};

const getApiUrl = () => {
  let envApiUrl = import.meta.env.VITE_API_BASE_URL ||
    import.meta.env.VITE_API_URL ||
    import.meta.env.VITE_DEV_API_URL;

  // P2-5: en produccion la variable solo se respeta si apunta al mismo origen o al apex exacto https://synclyst.com (sin subdominios);
  // cualquier otro dominio, localhost o el puerto 8000 se ignoran y se usa el mismo origen.
  if (envApiUrl && import.meta.env.PROD && !isAllowedProdApiUrl(envApiUrl, window.location.origin)) {
    envApiUrl = undefined;
  }

  if (envApiUrl) {
    return normalizeApiUrl(envApiUrl);
  }

  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : '';

  // Base central: sin variables, la API vive en el mismo origen (prod: https://synclyst.com/api; dev: proxy de Vite).
  return normalizeApiUrl(`${protocol}//${hostname}${port}`);
};

export const API_BASE_URL = getApiUrl();

export const handlePaymentRequired = (error: any) => {
  if (error?.response?.status === 402) {
    const errorData = error.response.data;
    const event = new CustomEvent('payment-required', {
      detail: {
        error: errorData,
        resourceType: errorData.error?.includes('property') ? 'property' : 'user'
      }
    });
    window.dispatchEvent(event);
    return Promise.reject(error);
  }
  return Promise.reject(error);
};

// ===== AXIOS INSTANCE SETUP =====
declare module 'axios' {
  interface AxiosRequestConfig {
    /** El llamador maneja el 401 en sitio: el interceptor no refresca, no borra tokens ni redirige. */
    skipAuthRedirect?: boolean;
  }
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// PRIMER interceptor de respuesta: detail objeto -> string seguro + detail_obj/errorCode (ver lib/apiError.ts).
installApiErrorNormalizer(apiClient);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => handlePaymentRequired(error)
);

// ===== TOKEN MANAGEMENT =====
export class TokenManager {
  public static readonly TOKEN_KEY = 'auth_token';
  public static readonly USER_KEY = 'auth_user';
  public static readonly REFRESH_KEY = 'refresh_token';

  static isTokenExpiringSoon(): boolean {
    try {
      const token = localStorage.getItem(this.TOKEN_KEY);
      if (!token) return true;

      const payload = JSON.parse(atob(token.split('.')[1]));
      const expTime = payload.exp * 1000;
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;

      return (expTime - now) < fiveMinutes;
    } catch (error) {
      console.warn('⚠️ Could not decode token expiration:', errorStatusForLog(error));
      return true;
    }
  }

  static async getValidToken(): Promise<string | null> {
    const token = localStorage.getItem(this.TOKEN_KEY);
    if (!token) return null;

    if (this.isTokenExpiringSoon()) {
      try {
        return await this.refreshToken();
      } catch (err) {
        console.error('Failed to auto-refresh expiring token:', errorStatusForLog(err));
        return null;
      }
    }
    return token;
  }

  static getValidTokenSync(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_KEY);
  }

  static getDecodedToken(): any {
    try {
      const token = localStorage.getItem(this.TOKEN_KEY);
      if (!token) return null;
      return JSON.parse(atob(token.split('.')[1]));
    } catch {
      return null;
    }
  }

  static async refreshToken(): Promise<string | null> {
    const refresh_token = localStorage.getItem(this.REFRESH_KEY);
    if (!refresh_token) {
      this.clearTokens();
      return null;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, { refresh_token }, {
        headers: { 'Content-Type': 'application/json' }
      });
      const newToken = response.data.access_token;
      if (newToken) {
        clearPlatformMfaToken(); // el backend invalida el X-MFA-Token con el refresh
        localStorage.setItem(this.TOKEN_KEY, newToken);
        localStorage.setItem('token', newToken); // Legacy compatibility
        return newToken;
      }
      return null;
    } catch (error) {
      console.error('Failed to refresh authentication token:', errorStatusForLog(error));
      this.clearTokens();
      return null;
    }
  }

  static clearTokens(): void {
    clearPlatformMfaToken();
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.REFRESH_KEY);
    localStorage.removeItem('token'); // Legacy compatibility
  }
}

// ===== REQUEST INTERCEPTOR =====
apiClient.interceptors.request.use(
  async (config) => {
    const requestUrl = (config.url || '').toString();
    const shouldSkipAuthHeader =
      requestUrl.includes('/api/auth/login') ||
      requestUrl.includes('/api/auth/refresh') ||
      requestUrl.includes('/api/auth/logout');

    if (!shouldSkipAuthHeader) {
      const token = await TokenManager.getValidToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // 2FA staff: X-MFA-Token solo en /api/platform/* y /api/internal-communications (nunca con ticket en URL o params).
    if (needsMfaHeader(requestUrl, config.baseURL || API_BASE_URL) && !hasTicketParam(config.params)) {
      const mfa = getPlatformMfaToken();
      if (mfa) config.headers[MFA_HEADER] = mfa;
      // Generacion del token con que salio la peticion: un 403 de una peticion anterior a verificar no borra el nuevo.
      (config as any).__mfaGen = getMfaGeneration();
    }
    // RBAC-12: employee no manda campos de asignación en PUT /api/tasks/{id} (Backend responde 403).
    applyEmployeeTaskPayloadRule(config);

    if (DEBUG_CONFIG.VERBOSE) {
      debugLog('API', '🌐 … …');
    }

    return config;
  },
  (error) => {
    debugError('API', '❌ Request interceptor error:', errorStatusForLog(error));
    return Promise.reject(error);
  }
);

// ===== RESPONSE INTERCEPTOR =====
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if (DEBUG_CONFIG.VERBOSE) {
      debugLog('API', '✅ … …: …');
    }
    return response;
  },
  async (error: AxiosError) => {
    if (DEBUG_CONFIG.API) {
      debugError('API', '❌ API response error:', errorStatusForLog(error));
    }

    const originalRequest = error.config as any;

    // 2FA staff: X-MFA-Token vencido / invalidado => volver a la pantalla de verificacion.
    // QA P2 (a08d835e): una peticion que salio sin token (o con el viejo) antes de verificar y vuelve con 403 despues
    // NO borra el token nuevo ni reabre la verificacion. El 403 se sigue rechazando igual (no se oculta).
    const staleMfa = typeof originalRequest?.__mfaGen === 'number' && originalRequest.__mfaGen !== getMfaGeneration();
    if (error.response?.status === 403 && mfaErrorCode(error) === 'MFA_REQUIRED' && !staleMfa) {
      clearPlatformMfaToken();
      // detail.url: para que cada pantalla decida si el 403 es suyo (Mensajes solo reacciona a internal-communications).
      window.dispatchEvent(new CustomEvent(MFA_REQUIRED_EVENT, { detail: { url: originalRequest?.url } }));
    } else if (error.response?.status === 403 && !staleMfa && typeof originalRequest?.url === 'string'
      && needsMfaHeader(originalRequest.url, originalRequest.baseURL || API_BASE_URL) && !hasTicketParam(originalRequest.params)) {
      // 403 generico en ruta 2FA: no se borra nada; el Gate (si esta montado) revisa /mfa/status. El 403 sigue igual.
      window.dispatchEvent(new CustomEvent(MFA_FORBIDDEN_EVENT, { detail: { url: originalRequest.url } }));
    }

    if (error.response && error.response.status === 402) {
      let role: string | null = null;
      try {
        const rawUser = localStorage.getItem(TokenManager.USER_KEY);
        if (rawUser) {
          const parsed = JSON.parse(rawUser);
          role = parsed?.role || null;
        }
      } catch {
        role = null;
      }

      const url = new URL(window.location.href);
      const alreadyLocked = url.searchParams.get('reason') === 'inactive';

      const isOwnerOrAdmin = role === 'owner' || role === 'admin';
      const redirectTo = isOwnerOrAdmin ? '/billing?reason=inactive' : '/profile?reason=inactive';

      if (!alreadyLocked && window.location.pathname !== '/login') {
        window.location.href = redirectTo;
      }
      return Promise.reject(error);
    }

    if (error.response && (error.response.status === 405 || error.response.status === 500)) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      const requestUrl = (error.config?.url || '').toString();
      const shouldSkipAuthRefresh =
        requestUrl.includes('/api/auth/login') ||
        requestUrl.includes('/api/auth/refresh');

      // Opt-in por request (p. ej. /condo-invite): el llamador maneja el 401; no refresh, no clearTokens, no redirect.
      if (shouldSkipAuthRefresh || originalRequest?.skipAuthRedirect) {
        return Promise.reject(error);
      }

      if (!originalRequest._retry) {
        if (typeof originalRequest._retryCount === 'undefined') {
          originalRequest._retryCount = 0;
        }

        const MAX_RETRIES = 3;

        if (originalRequest._retryCount < MAX_RETRIES) {
          originalRequest._retry = true;
          originalRequest._retryCount++;

          let newToken: string | null = null;
          try {
            newToken = await TokenManager.refreshToken();
          } catch {
            newToken = null;
          }

          if (newToken && originalRequest) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            originalRequest._retry = false;
            return apiClient(originalRequest);
          }
        }

        TokenManager.clearTokens();

        if (!window.location.pathname.includes('/login') &&
          !window.location.pathname.includes('/register') &&
          window.location.pathname !== '/') {
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

// ===== COMPATIBILITY API CLIENT METHODS (For AuthContext) =====
export const api = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/api/auth/login', credentials);
    if (response.data.access_token) {
      localStorage.setItem('auth_token', response.data.access_token);
      localStorage.setItem('token', response.data.access_token);
    }
    return response.data;
  },

  getCurrentUser: async (): Promise<User> => {
    try {
      debugLog('API', '👤 Getting current user...');
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await apiClient.get('/api/auth/me');

      if (DEBUG_CONFIG.VERBOSE) {
        debugLog('API', '✅ User retrieved', '(…)');
      }

      return response.data;
    } catch (error: any) {
      debugError('API', '❌ Error getting current user:', errorStatusForLog(error));
      if (error.response?.status === 401) {
        TokenManager.clearTokens();
        throw new Error('Authentication expired. Please log in again.');
      }
      throw error;
    }
  },

  logout: async (): Promise<void> => {
    console.log('🔓 Direct logout method called...');
    try {
      await apiClient.post('/api/auth/logout');
      console.log('✅ Logout API call successful');
    } catch (error) {
      console.warn('⚠️ Logout API call failed (clearing tokens anyway):', errorStatusForLog(error));
    } finally {
      TokenManager.clearTokens();
    }
  }
};
